import { AndroidLogo, AppleLogo, ArrowLeft, ArrowRight, CheckCircle, Star, WarningCircleIcon } from "@phosphor-icons/react";
import { Button, Spinner } from "flowbite-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AnimatedModal from "../../../../components/AnimatedModal";
import { httpService } from "../../../../services/httpService";
import { API_CONFIG } from "../../../../shared/constants";
import { DuplicateApp, DuplicateAppGroup, LinkAppsResponse } from "../interface";

interface Props {
  isOpen: boolean;
  groups: DuplicateAppGroup[];
  onClose: () => void;
  onResolved: (resolvedStoreIds: string[]) => void;
}

function PlatformBadge({ platform }: { platform: DuplicateApp["google_admob_app_platform"] }) {
  const isAndroid = platform === "ANDROID";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isAndroid
        ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
        }`}
    >
      {isAndroid ? <AndroidLogo className="h-3 w-3" /> : <AppleLogo className="h-3 w-3" />}
      {platform}
    </span>
  );
}

function AppLogoPlaceholder({ platform }: { platform: DuplicateApp["google_admob_app_platform"] }) {
  return (
    <div className="h-10 w-10 rounded-xl bg-gray-100 border border-gray-200 dark:bg-gray-700 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
      {platform === "ANDROID" ? (
        <AndroidLogo className="h-5 w-5 text-gray-400 dark:text-gray-500" />
      ) : (
        <AppleLogo className="h-5 w-5 text-gray-400 dark:text-gray-500" />
      )}
    </div>
  );
}

const getRecommendedAppId = (apps: DuplicateApp[]): number | null => {
  const maxRevenue = Math.max(...apps.map((a) => a.last_30_day_revenue));
  if (maxRevenue === 0) return null;
  const topApps = apps.filter((a) => a.last_30_day_revenue === maxRevenue);
  if (topApps.length > 1) return null;
  return topApps[0].id;
};

export default function DuplicateAppsWizardModal({ isOpen, groups, onClose, onResolved }: Props) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selections, setSelections] = useState<Record<string, number>>({});
  const [isSaving, setIsSaving] = useState(false);

  const totalGroups = groups.length;
  const currentGroup = groups[currentIndex] ?? groups[0];
  const selectedCount = Object.keys(selections).length;

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    } else {
      setSelections({});
      setCurrentIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentIndex >= groups.length && groups.length > 0) {
      setCurrentIndex(groups.length - 1);
    }
  }, [groups.length, currentIndex]);

  // Auto-select the recommended app when navigating to a group with no selection yet
  useEffect(() => {
    if (!currentGroup) return;
    const storeId = currentGroup.google_admob_store_id;
    if (selections[storeId] !== undefined) return;
    const recommended = getRecommendedAppId(currentGroup.apps);
    if (recommended !== null) {
      setSelections((prev) => ({ ...prev, [storeId]: recommended }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, isOpen]);

  if (!currentGroup) return null;

  const representativeApp = currentGroup.apps[0];
  const currentSelection = selections[currentGroup.google_admob_store_id];

  const handleSelect = (storeId: string, appId: number) => {
    setSelections((prev) => ({ ...prev, [storeId]: appId }));
  };

  const handleSave = async () => {
    const idsToLink: number[] = [];
    const resolvedStoreIds: string[] = [];

    for (const group of groups) {
      const selectedId = selections[group.google_admob_store_id];
      if (selectedId === undefined) continue;
      resolvedStoreIds.push(group.google_admob_store_id);
      idsToLink.push(selectedId);
    }

    if (idsToLink.length === 0) return;

    setIsSaving(true);
    try {
      await httpService.patch<LinkAppsResponse>(API_CONFIG.path.linkApps, { ids: idsToLink });
      const appWord = resolvedStoreIds.length === 1 ? "app" : "apps";
      toast.success(`${resolvedStoreIds.length} duplicate ${appWord} resolved successfully.`);
      setSelections((prev) => {
        const next = { ...prev };
        resolvedStoreIds.forEach((id) => delete next[id]);
        return next;
      });
      setCurrentIndex(0);
      onResolved(resolvedStoreIds);
    } catch (err: unknown) {
      const errorMsg =
        (err as { error?: string })?.error ??
        "Failed to link apps. Please try again.";
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-between w-full">
      <Button color="gray" size="sm" onClick={onClose} disabled={isSaving}>
        {t("configurations.integrations.resolveLater", "Resolve Later")}
      </Button>
      <div className="flex items-center gap-2">
        {currentIndex > 0 && (
          <Button
            color="gray"
            size="sm"
            onClick={() => setCurrentIndex((i) => i - 1)}
            disabled={isSaving}
          >
            <div className="flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" />
              {t("common.back", "Back")}
            </div>
          </Button>
        )}
        {currentIndex < totalGroups - 1 && (
          <Button
            color="indigo"
            outline
            size="sm"
            onClick={() => setCurrentIndex((i) => i + 1)}
            disabled={isSaving}
          >
            <div className="flex items-center gap-1">
              {t("common.next", "Next")}
              <ArrowRight className="h-4 w-4" />
            </div>
          </Button>
        )}
        <Button
          color="indigo"
          size="sm"
          onClick={handleSave}
          disabled={selectedCount === 0 || isSaving}
          isProcessing={isSaving}
          processingSpinner={<Spinner color="white" light size="sm" className="-mt-[2px]" />}
        >
          {selectedCount > 0
            ? t("configurations.integrations.saveSelected", `Save (${selectedCount} of ${totalGroups} selected)`, { count: selectedCount, total: totalGroups })
            : t("configurations.integrations.saveSelectedEmpty", "Save Selected")}
        </Button>
      </div>
    </div>
  );

  return (
    <AnimatedModal
      show={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <WarningCircleIcon className="h-5 w-5 text-amber-500" weight="fill" />
          <span>{t("configurations.integrations.duplicateAppsTitle", "Duplicate Apps Detected")}</span>
          <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {totalGroups} {totalGroups === 1
              ? t("configurations.integrations.appAffected", "app affected")
              : t("configurations.integrations.appsAffected", "apps affected")}
          </span>
        </div>
      }
      footer={footer}
    >
      <div className="flex gap-0 min-h-[340px]">
        {/* Left panel — all duplicate app groups */}
        <div className="w-[38%] flex-shrink-0 border-r border-gray-100 dark:border-neutral-700 pr-3 mr-3 overflow-y-auto max-h-[420px]">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
            {t("configurations.integrations.allDuplicates", "All Duplicates")}
          </p>
          <div className="space-y-1.5 p-0.5">
            {groups.map((group, i) => {
              const isActive = i === currentIndex;
              const isResolved = selections[group.google_admob_store_id] !== undefined;
              const groupApp = group.apps[0];
              return (
                <button
                  key={group.google_admob_store_id}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl text-left transition-all ${isActive
                    ? "bg-indigo-50 dark:bg-indigo-500/15 ring-1 ring-indigo-400 dark:ring-indigo-500"
                    : "hover:bg-gray-50 dark:hover:bg-neutral-700/50"
                    }`}
                >
                  {/* App icon */}
                  <div className="flex-shrink-0">
                    {groupApp.google_admob_app_logo ? (
                      <img
                        src={groupApp.google_admob_app_logo}
                        alt={groupApp.google_admob_app_name}
                        className="h-9 w-9 rounded-lg object-cover"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <AppLogoPlaceholder platform={groupApp.google_admob_app_platform} />
                    )}
                  </div>
                  {/* App info */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate leading-tight ${isActive ? "text-indigo-700 dark:text-indigo-300" : "text-gray-800 dark:text-gray-200"}`}>
                      {groupApp.google_admob_app_name}
                    </p>
                    <div className="mt-1">
                      <PlatformBadge platform={groupApp.google_admob_app_platform} />
                    </div>
                  </div>
                  {/* Status indicator */}
                  <div className="flex-shrink-0">
                    {isResolved ? (
                      <CheckCircle className="h-4 w-4 text-green-500" weight="fill" />
                    ) : (
                      <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right panel — resolution UI for selected group */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* App info card */}
          <div className="flex items-center gap-3 p-3.5 bg-amber-50 border border-amber-200 dark:bg-amber-900/10 dark:border-amber-700/40 rounded-xl mb-4">
            {representativeApp.google_admob_app_logo ? (
              <img
                src={representativeApp.google_admob_app_logo}
                alt={representativeApp.google_admob_app_name}
                className="h-12 w-12 rounded-xl object-cover flex-shrink-0"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
              />
            ) : (
              <AppLogoPlaceholder platform={representativeApp.google_admob_app_platform} />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                {representativeApp.google_admob_app_name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5 truncate">
                {currentGroup.google_admob_store_id}
              </p>
              <div className="mt-1">
                <PlatformBadge platform={representativeApp.google_admob_app_platform} />
              </div>
            </div>
          </div>

          {/* Instructions */}
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            {t("configurations.integrations.duplicateInstructions", "This app exists in")}{" "}
            <span className="font-semibold text-gray-800 dark:text-gray-100">
              {currentGroup.apps.length} {t("configurations.integrations.admobAccounts", "AdMob accounts")}
            </span>
            {". "}{t("configurations.integrations.selectAccountInstruction", "Select the account you want to link — it will be kept active in your reports.")}
          </p>

          {/* Account radio options */}
          <div className="space-y-2">
            {currentGroup.apps.map((app) => {
              const isSelected = currentSelection === app.id;
              const recommendedAppId = getRecommendedAppId(currentGroup.apps);
              const isRecommended = recommendedAppId === app.id;
              return (
                <button
                  key={app.id}
                  onClick={() => handleSelect(currentGroup.google_admob_store_id, app.id)}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${isSelected
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-500/20 dark:border-indigo-400"
                    : "border-gray-200 dark:border-neutral-600 hover:border-gray-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-700"
                    }`}
                >
                  <div
                    className={`h-4 w-4 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isSelected
                      ? "border-indigo-500 bg-indigo-500"
                      : "border-gray-400 dark:border-neutral-500 bg-white dark:bg-neutral-600"
                      }`}
                  >
                    {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-sm font-medium truncate ${isSelected ? "text-indigo-700 dark:text-indigo-300" : "text-gray-800 dark:text-gray-200"}`}>
                        {app.social_account.email}
                      </p>
                      {isRecommended && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex-shrink-0">
                          <Star className="h-2.5 w-2.5" weight="fill" />
                          {t("configurations.integrations.recommended", "Recommended")}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs font-mono mt-0.5 truncate ${isSelected ? "text-indigo-500 dark:text-indigo-400" : "text-gray-400 dark:text-gray-400"}`}>
                      {app.social_account.admob_publisher_id}
                    </p>
                    {isRecommended && (
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                        {t("configurations.integrations.recommendedNote", "Highest revenue in last 30 days")}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <CheckCircle className="h-5 w-5 text-indigo-500 dark:text-indigo-400 flex-shrink-0" weight="fill" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Resolve-later hint */}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-4 text-center">
            {t("configurations.integrations.resolveLaterHint")}
          </p>
        </div>
      </div>
    </AnimatedModal>
  );
}
